import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, Check } from "lucide-react";

// Comprehensive, professionally categorized organization taxonomy.
// The selected value is the exact label string (stored structured server-side,
// which derives the governance category). Groups are presentation only.
export const ORG_TYPE_GROUPS = [
  { group: "Government & Public Sector", items: ["Government Organization", "Central Government", "State Government", "Local Government / Municipal Organization", "Public-Sector Organization", "Government Agency", "Government-Owned Enterprise"] },
  { group: "Defense & Security", items: ["Army / Military Organization", "Defense Organization", "Defense Contractor", "Intelligence / National Security Organization", "Law Enforcement / Police", "Emergency Services", "Cybersecurity Company", "Security Services Organization"] },
  { group: "Financial Services", items: ["Bank", "Insurance Company", "Financial Institution", "FinTech Company", "Investment / Asset Management", "Payment Services / Payment Provider"] },
  { group: "Technology & Digital", items: ["Technology Company", "Software / SaaS Company", "AI / Machine Learning Company", "IT Services Company", "Cloud / Infrastructure Provider", "Telecommunications Company", "Internet / Digital Services Company", "Data / Analytics Company", "Startup"] },
  { group: "Media & Creator Economy", items: ["Creator / Influencer", "Media Company", "News / Publishing Organization", "Entertainment Company", "Production House", "Digital Creator / Content Creator", "Gaming Company"] },
  { group: "Healthcare & Life Sciences", items: ["Hospital / Healthcare Organization", "Pharmaceutical Company", "Biotechnology Company", "Medical Research Organization", "HealthTech Company"] },
  { group: "Education & Research", items: ["Educational Institution", "University / College", "School", "Research Organization", "Training / EdTech Organization"] },
  { group: "Business & Industry", items: ["Private Company", "Public Company", "Manufacturing Company", "Retail / E-Commerce", "Logistics / Transportation", "Construction / Infrastructure", "Energy / Utilities", "Real Estate", "Agriculture / Agritech", "Automotive", "Aerospace", "Space / Space Technology"] },
  { group: "Social & Non-Profit", items: ["NGO", "Non-Profit Organization", "Charity / Foundation", "Social Organization", "Community Organization", "Professional Association"] },
  { group: "Professional Services", items: ["Consulting Company", "Legal Organization / Law Firm", "Accounting / CA Firm", "HR / Recruitment Organization", "Marketing / Advertising Agency", "Professional Services"] },
  { group: "Other", items: ["Individual", "Other"] },
];

// Creator/individual values that trigger the simplified individual onboarding.
export const INDIVIDUAL_TYPES = ["Individual", "Creator / Influencer", "Digital Creator / Content Creator"];

export default function OrgTypeSelect({ value, onChange, testid = "reg-type" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rect, setRect] = useState(null);
  const btn = useRef(null);
  const panel = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (btn.current?.contains(e.target) || panel.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = () => {
    if (!open && btn.current) {
      const r = btn.current.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom + 6, width: r.width });
    }
    setOpen((o) => !o); setQ("");
  };

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return ORG_TYPE_GROUPS;
    return ORG_TYPE_GROUPS.map((g) => ({ group: g.group, items: g.items.filter((it) => it.toLowerCase().includes(needle)) }))
      .filter((g) => g.items.length);
  }, [q]);

  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <>
      <button ref={btn} type="button" data-testid={testid} onClick={toggle}
        className="flex h-11 w-full items-center justify-between rounded-lg border border-slate-800 bg-slate-900/60 px-3.5 text-sm text-slate-100 outline-none transition-colors hover:border-slate-700 focus:border-blue-500/60">
        <span className={value ? "text-slate-100" : "text-slate-500"}>{value || "Select organization type"}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && rect && createPortal(
        <div ref={panel} data-testid="org-type-panel" style={{ left: rect.left, top: rect.top, width: Math.max(rect.width, 320) }}
          className="fixed z-[90] max-h-[60vh] overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
          <div className="flex items-center gap-2 border-b border-slate-800 px-3 py-2.5">
            <Search className="h-4 w-4 text-slate-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="org-type-search"
              placeholder="Search organization type…" className="w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-600 outline-none" />
          </div>
          <div className="max-h-[52vh] overflow-y-auto py-1">
            {groups.length === 0 && <p className="px-4 py-6 text-center text-sm text-slate-500">No matching type</p>}
            {groups.map((g) => (
              <div key={g.group} className="py-1">
                <p className="sticky top-0 bg-slate-900 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-slate-500">{g.group}</p>
                {g.items.map((it) => (
                  <button key={it} type="button" onClick={() => pick(it)} data-testid={`org-type-opt-${it}`}
                    className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition-colors hover:bg-slate-800/70 ${value === it ? "text-blue-300" : "text-slate-300"}`}>
                    <span>{it}</span>{value === it && <Check className="h-4 w-4 text-blue-400" />}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>, document.body)}
    </>
  );
}
